import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { 
  Plus, X, TrendingUp, Home, DollarSign, ArrowRight,
  CheckCircle, Clock, AlertCircle, BarChart3, Calculator, Trash2,
  Search, Building2, ExternalLink
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { marketplaceAPI } from '../services/api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PIPELINE_STAGES = [
  { id: 'prospect', label: 'Prospect', color: 'bg-gray-500' },
  { id: 'negotiating', label: 'Negotiating', color: 'bg-amber-500' },
  { id: 'approved', label: 'Approved', color: 'bg-green-500' },
  { id: 'live', label: 'Live', color: 'bg-[#1F3A68]' }
];

const DealSourcing = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getAuthToken } = useAuth();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [marketplaceStats, setMarketplaceStats] = useState({ listings: 0, pendingOffers: 0, acceptedOffers: 0 });
  
  const [newDeal, setNewDeal] = useState({
    address: '', city: '', postcode: '',
    bedrooms: 2, bathrooms: 1,
    estimatedNightlyRate: 100, estimatedOccupancy: 70,
    monthlyRent: 1000, deposit: 2000, contractLength: 12, rentFreePeriod: 0,
    councilTax: 150, electricity: 80, water: 30, internet: 30, serviceCharge: 0,
    cleaningPerStay: 50, linenPerStay: 20, setupCosts: 500, notes: ''
  });

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API_URL}/api/deals/`, { headers: { Authorization: `Bearer ${token}` }});
      setDeals(res.data);
      
      // Also fetch marketplace stats (sent offers)
      try {
        const offers = await marketplaceAPI.getSentOffers(token);
        setMarketplaceStats({
          listings: 0, // Would need separate call
          pendingOffers: offers.filter(o => o.status === 'pending').length,
          acceptedOffers: offers.filter(o => o.status === 'accepted').length
        });
      } catch (e) { /* marketplace might not have data yet */ }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [getAuthToken]);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  const createDeal = async () => {
    try {
      const token = getAuthToken();
      await axios.post(`${API_URL}/api/deals/`, newDeal, { headers: { Authorization: `Bearer ${token}` }});
      toast({ title: 'Success', description: 'Deal created' });
      setShowCreateModal(false);
      resetForm();
      fetchDeals();
    } catch (e) { toast({ title: 'Error', description: 'Failed to create deal', variant: 'destructive' }); }
  };

  const updateStatus = async (dealId, status) => {
    try {
      const token = getAuthToken();
      await axios.patch(`${API_URL}/api/deals/${dealId}/status?status=${status}`, {}, { headers: { Authorization: `Bearer ${token}` }});
      toast({ title: 'Success', description: `Status updated to ${status}` });
      fetchDeals();
    } catch (e) { toast({ title: 'Error', description: 'Failed to update', variant: 'destructive' }); }
  };

  const convertToProperty = async (dealId) => {
    try {
      const token = getAuthToken();
      await axios.post(`${API_URL}/api/deals/${dealId}/convert`, {}, { headers: { Authorization: `Bearer ${token}` }});
      toast({ title: 'Success', description: 'Deal converted to property!' });
      fetchDeals();
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to convert', variant: 'destructive' }); }
  };

  const resetForm = () => {
    setNewDeal({
      address: '', city: '', postcode: '', bedrooms: 2, bathrooms: 1,
      estimatedNightlyRate: 100, estimatedOccupancy: 70,
      monthlyRent: 1000, deposit: 2000, contractLength: 12, rentFreePeriod: 0,
      councilTax: 150, electricity: 80, water: 30, internet: 30, serviceCharge: 0,
      cleaningPerStay: 50, linenPerStay: 20, setupCosts: 500, notes: ''
    });
  };

  // Summary metrics
  const totalProjectedProfit = deals.reduce((sum, d) => sum + (d.projections?.monthlyProfit || 0), 0);
  const avgROI = deals.length > 0 ? deals.reduce((sum, d) => sum + (d.projections?.annualROI || 0), 0) / deals.length : 0;
  const totalInvestment = deals.reduce((sum, d) => sum + (d.projections?.totalInvestment || 0), 0);

  const dealsByStage = PIPELINE_STAGES.reduce((acc, stage) => {
    acc[stage.id] = deals.filter(d => d.status === stage.id);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* R2R Marketplace Banner */}
      <Card className="bg-gradient-to-r from-[#1F3A68] via-[#2d5a9e] to-[#1F3A68] text-white overflow-hidden">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Building2 size={32} />
              </div>
              <div>
                <h3 className="text-xl font-bold">R2R Deal Marketplace</h3>
                <p className="text-white/80 text-sm">Browse landlord listings and make offers on new R2R opportunities</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              {marketplaceStats.pendingOffers > 0 && (
                <div className="text-center">
                  <div className="text-2xl font-bold">{marketplaceStats.pendingOffers}</div>
                  <div className="text-white/70 text-xs">Pending Offers</div>
                </div>
              )}
              {marketplaceStats.acceptedOffers > 0 && (
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-300">{marketplaceStats.acceptedOffers}</div>
                  <div className="text-white/70 text-xs">Accepted</div>
                </div>
              )}
              <Button 
                onClick={() => navigate('/host/deals/marketplace')}
                className="bg-white text-[#1F3A68] hover:bg-white/90 font-semibold"
                data-testid="browse-marketplace-btn"
              >
                <Search size={16} className="mr-2" />
                Browse Marketplace
                <ExternalLink size={14} className="ml-2" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#1F3A68]">Deal Pipeline</h2>
          <p className="text-gray-600 text-sm">Track your R2R deals and profit projections</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-[#1F3A68]" data-testid="add-deal-btn">
          <Plus size={16} className="mr-2" /> Add Deal
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-[#1F3A68] to-[#1F3A68]/80 text-white" data-testid="deal-total-profit">
          <CardContent className="p-4">
            <DollarSign size={24} className="mb-2" />
            <div className="text-2xl font-bold">£{totalProjectedProfit.toLocaleString()}</div>
            <div className="text-white/80 text-sm">Est. Monthly Profit</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white" data-testid="deal-avg-roi">
          <CardContent className="p-4">
            <TrendingUp size={24} className="mb-2" />
            <div className="text-2xl font-bold">{avgROI.toFixed(1)}%</div>
            <div className="text-white/80 text-sm">Avg Annual ROI</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white" data-testid="deal-total-investment">
          <CardContent className="p-4">
            <BarChart3 size={24} className="mb-2" />
            <div className="text-2xl font-bold">£{totalInvestment.toLocaleString()}</div>
            <div className="text-white/80 text-sm">Total Investment</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-[#CB3B3B] to-[#CB3B3B]/80 text-white" data-testid="deal-total-count">
          <CardContent className="p-4">
            <Home size={24} className="mb-2" />
            <div className="text-2xl font-bold">{deals.length}</div>
            <div className="text-white/80 text-sm">Total Deals</div>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline View */}
      <div className="grid md:grid-cols-4 gap-4" data-testid="deal-pipeline">
        {PIPELINE_STAGES.map(stage => (
          <div key={stage.id} data-testid={`pipeline-stage-${stage.id}`}>
            <div className={`${stage.color} text-white px-3 py-2 rounded-t-lg font-semibold flex items-center justify-between`}>
              <span>{stage.label}</span>
              <span className="bg-white/20 px-2 py-0.5 rounded-full text-sm">{dealsByStage[stage.id]?.length || 0}</span>
            </div>
            <div className="bg-gray-50 rounded-b-lg p-2 min-h-[200px] space-y-2">
              {dealsByStage[stage.id]?.map(deal => (
                <Card 
                  key={deal._id} 
                  className="cursor-pointer hover:shadow-md transition-shadow" 
                  onClick={() => setSelectedDeal(deal)}
                  data-testid={`deal-card-${deal._id}`}
                >
                  <CardContent className="p-3">
                    <h4 className="font-semibold text-sm truncate">{deal.address}</h4>
                    <p className="text-xs text-gray-600">{deal.city}</p>
                    <div className="mt-2 text-xs">
                      <span className="text-green-600 font-semibold">£{deal.projections?.monthlyProfit?.toFixed(0)}/mo</span>
                      <span className="text-gray-500 ml-2">{deal.projections?.annualROI?.toFixed(0)}% ROI</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {(!dealsByStage[stage.id] || dealsByStage[stage.id].length === 0) && (
                <p className="text-center text-gray-400 text-sm py-8">No deals</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create Deal Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl my-8">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Add New Deal</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowCreateModal(false)}><X size={20} /></Button>
            </CardHeader>
            <CardContent className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2"><Label>Address</Label><Input value={newDeal.address} onChange={e => setNewDeal(p => ({...p, address: e.target.value}))} /></div>
                <div><Label>City</Label><Input value={newDeal.city} onChange={e => setNewDeal(p => ({...p, city: e.target.value}))} /></div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div><Label>Postcode</Label><Input value={newDeal.postcode} onChange={e => setNewDeal(p => ({...p, postcode: e.target.value}))} /></div>
                <div><Label>Bedrooms</Label><Input type="number" value={newDeal.bedrooms} onChange={e => setNewDeal(p => ({...p, bedrooms: +e.target.value}))} /></div>
                <div><Label>Bathrooms</Label><Input type="number" value={newDeal.bathrooms} onChange={e => setNewDeal(p => ({...p, bathrooms: +e.target.value}))} /></div>
                <div><Label>Est. Nightly (£)</Label><Input type="number" value={newDeal.estimatedNightlyRate} onChange={e => setNewDeal(p => ({...p, estimatedNightlyRate: +e.target.value}))} /></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Occupancy %</Label><Input type="number" value={newDeal.estimatedOccupancy} onChange={e => setNewDeal(p => ({...p, estimatedOccupancy: +e.target.value}))} /></div>
                <div><Label>Monthly Rent (£)</Label><Input type="number" value={newDeal.monthlyRent} onChange={e => setNewDeal(p => ({...p, monthlyRent: +e.target.value}))} /></div>
                <div><Label>Deposit (£)</Label><Input type="number" value={newDeal.deposit} onChange={e => setNewDeal(p => ({...p, deposit: +e.target.value}))} /></div>
              </div>
              <h4 className="font-semibold border-t pt-4">Fixed Costs (Monthly)</h4>
              <div className="grid grid-cols-5 gap-4">
                <div><Label>Council Tax</Label><Input type="number" value={newDeal.councilTax} onChange={e => setNewDeal(p => ({...p, councilTax: +e.target.value}))} /></div>
                <div><Label>Electricity</Label><Input type="number" value={newDeal.electricity} onChange={e => setNewDeal(p => ({...p, electricity: +e.target.value}))} /></div>
                <div><Label>Water</Label><Input type="number" value={newDeal.water} onChange={e => setNewDeal(p => ({...p, water: +e.target.value}))} /></div>
                <div><Label>Internet</Label><Input type="number" value={newDeal.internet} onChange={e => setNewDeal(p => ({...p, internet: +e.target.value}))} /></div>
                <div><Label>Service Charge</Label><Input type="number" value={newDeal.serviceCharge} onChange={e => setNewDeal(p => ({...p, serviceCharge: +e.target.value}))} /></div>
              </div>
              <h4 className="font-semibold border-t pt-4">Variable Costs (Per Stay)</h4>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Cleaning (£)</Label><Input type="number" value={newDeal.cleaningPerStay} onChange={e => setNewDeal(p => ({...p, cleaningPerStay: +e.target.value}))} /></div>
                <div><Label>Linen (£)</Label><Input type="number" value={newDeal.linenPerStay} onChange={e => setNewDeal(p => ({...p, linenPerStay: +e.target.value}))} /></div>
                <div><Label>Setup Costs (£)</Label><Input type="number" value={newDeal.setupCosts} onChange={e => setNewDeal(p => ({...p, setupCosts: +e.target.value}))} /></div>
              </div>
              <div><Label>Notes</Label><Textarea value={newDeal.notes} onChange={e => setNewDeal(p => ({...p, notes: e.target.value}))} rows={2} /></div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1">Cancel</Button>
                <Button onClick={createDeal} className="flex-1 bg-[#1F3A68]">Create Deal</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Deal Detail Modal */}
      {selectedDeal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Deal Details</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setSelectedDeal(null)}><X size={20} /></Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="text-xl font-bold">{selectedDeal.address}</h3>
                <p className="text-gray-600">{selectedDeal.city}, {selectedDeal.postcode}</p>
                <p className="text-sm text-gray-500">{selectedDeal.bedrooms} bed, {selectedDeal.bathrooms} bath</p>
              </div>
              <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                <div><span className="text-gray-600 text-sm">Monthly Revenue</span><div className="font-bold text-lg">£{selectedDeal.projections?.monthlyRevenue?.toLocaleString()}</div></div>
                <div><span className="text-gray-600 text-sm">Monthly Costs</span><div className="font-bold text-lg text-red-600">£{(selectedDeal.projections?.monthlyFixedCosts + selectedDeal.projections?.monthlyVariableCosts)?.toLocaleString()}</div></div>
                <div><span className="text-gray-600 text-sm">Monthly Profit</span><div className="font-bold text-lg text-green-600">£{selectedDeal.projections?.monthlyProfit?.toLocaleString()}</div></div>
                <div><span className="text-gray-600 text-sm">Annual ROI</span><div className="font-bold text-lg text-[#1F3A68]">{selectedDeal.projections?.annualROI?.toFixed(1)}%</div></div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {selectedDeal.status === 'prospect' && <Button onClick={() => updateStatus(selectedDeal._id, 'negotiating')} variant="outline">Move to Negotiating</Button>}
                {selectedDeal.status === 'negotiating' && <Button onClick={() => updateStatus(selectedDeal._id, 'approved')} className="bg-green-600">Approve Deal</Button>}
                {selectedDeal.status === 'approved' && <Button onClick={() => convertToProperty(selectedDeal._id)} className="bg-[#1F3A68]"><Home size={16} className="mr-1" />Convert to Property</Button>}
                {selectedDeal.status !== 'live' && <Button onClick={() => updateStatus(selectedDeal._id, 'rejected')} variant="outline" className="text-red-600">Reject</Button>}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DealSourcing;
